/**
 * Transitions reporting (ADR 0104): a node that the hub welcomed with `transitions: true`
 * sends a session only when something state-relevant changed (`transitionKey`), as a summary
 * (`summarizeSession`: no subagent or workflow lists, which are the bulk of a session), and
 * sends the counters that move all the time (tokens, turns, tool calls, last activity, live
 * usage) once a minute in one `rollup` message for every session that moved. The server
 * merges a rollup into the summary it holds (`applyRollupRow`), so every module keeps reading
 * Session objects. Detail (the lists, the full last text) is fetched from the node on demand.
 *
 * Wire cost: a few patches per session over its life plus one small rollup a minute per
 * machine, instead of one whole Session per second while it works.
 */
import type { Id, RollupRow, Session } from '../index.ts';

export const ROLLUP_INTERVAL_MS = 60_000;
/** Rows per rollup; a machine with more moving sessions sends the rest in the next one. */
export const ROLLUP_MAX_ROWS = 500;

/** Marks a Session the server holds as a summary (detail is on the node). */
export const SUMMARY_FLAG = 'summary';

/**
 * What a transition is: these are the fields a change of which the server must learn now.
 * Counters and timestamps are left out on purpose; they travel in rollups.
 */
export function transitionKey(s: Session): string {
  const ext = s.ext ?? {};
  return JSON.stringify([
    s.status,
    s.state ?? null,
    s.title,
    s.projectId,
    s.checkoutId,
    s.cwd,
    s.model,
    s.permissionMode,
    s.pid,
    s.origin,
    s.agentKind ?? null,
    s.partial ?? false,
    runningCount(s.subagents),
    runningCount(s.workflows),
    s.subagents.length,
    s.workflows.length,
    ext.asks ?? null,
    ext.askAnswers ?? null,
    ext.failure ?? null,
    ext.account ?? null,
    ext.testRun ?? null,
    // The text matters when it is an ask or a final report: a waiting or ended session's last
    // text, and for an idle one only whether it ends in a question (the console's Asked state).
    // A working or idle session's text changing is a counter move, not a transition.
    textKey(s),
  ]);
}

/** What of the last text is state (see transitionKey). */
export function textKey(s: Pick<Session, 'status' | 'lastText'>): string {
  if (s.status === 'waiting' || s.status === 'ended') return s.lastText;
  if (s.status === 'idle') return /\?\s*$/.test(s.lastText) ? 'asked' : '';
  return '';
}

function runningCount(list: readonly { status: string }[]): number {
  let n = 0;
  for (const x of list) if (x.status === 'running') n++;
  return n;
}

/**
 * The session as the server keeps it: everything but the subagent and workflow lists, with
 * their sizes and how many run kept so the lists' readers (stuck checks, "spawn" flows) still
 * know. `ext.summary` says the detail is on the node. The live usage stays: it is small.
 */
export function summarizeSession(s: Session): Session {
  const running = { subagents: runningCount(s.subagents), workflows: runningCount(s.workflows) };
  return {
    ...s,
    subagents: [],
    workflows: [],
    subagentsTotal: s.subagentsTotal ?? s.subagents.length,
    workflowsTotal: s.workflowsTotal ?? s.workflows.length,
    ext: { ...(s.ext ?? {}), [SUMMARY_FLAG]: true, running },
  };
}

/** True when the server holds only a summary of this session. */
export function isSummary(s: Pick<Session, 'ext'>): boolean {
  return s.ext?.[SUMMARY_FLAG] === true;
}

/** How many subagents and workflows run, from a summary or a full session. */
export function runningIn(s: Session): { subagents: number; workflows: number } {
  const r = s.ext?.running as { subagents?: unknown; workflows?: unknown } | undefined;
  if (r && typeof r === 'object') return { subagents: num(r.subagents), workflows: num(r.workflows) };
  return { subagents: runningCount(s.subagents), workflows: runningCount(s.workflows) };
}

function num(x: unknown): number {
  return typeof x === 'number' && Number.isFinite(x) && x >= 0 ? Math.floor(x) : 0;
}

/** The rollup row of a session: its counters now. */
export function rollupRowOf(s: Session): RollupRow {
  const live = s.ext?.usage;
  return {
    id: s.id,
    lastActivity: s.lastActivity,
    turns: s.turns,
    toolCalls: s.toolCalls,
    tokens: s.tokens,
    ...(s.usage ? { usage: s.usage } : {}),
    ...(live !== undefined ? { liveUsage: live } : {}),
    ...(s.partial ? { partial: true } : {}),
  };
}

/** What of a session a rollup carries, for "did the counters move" checks. */
export function rollupKey(s: Session): string {
  return JSON.stringify(rollupRowOf(s));
}

/**
 * The session with a rollup row applied: a new object (the server caches each entity's JSON
 * by identity, so nothing is edited in place). Counters only ever replace; a row older than
 * what the server holds (lastActivity behind) is still applied, since the node is the source.
 */
export function applyRollupRow(s: Session, r: RollupRow): Session {
  const ext = r.liveUsage !== undefined ? { ...(s.ext ?? {}), usage: r.liveUsage } : s.ext;
  return {
    ...s,
    lastActivity: r.lastActivity,
    turns: r.turns,
    toolCalls: r.toolCalls,
    tokens: r.tokens,
    ...(r.usage ? { usage: r.usage } : {}),
    ...(r.partial !== undefined ? { partial: r.partial } : {}),
    ...(ext !== undefined ? { ext } : {}),
  };
}

const MAX_TOKENS = 1e12;
/** A millisecond timestamp (year 5138 at most). */
const MAX_TS = 1e14;

/** A rollup row as a node sent it, with anything malformed dropped; null when it is not a row. */
export function cleanRollupRow(x: unknown): RollupRow | null {
  if (!x || typeof x !== 'object') return null;
  const r = x as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id || r.id.length > 200) return null;
  const t = r.tokens as Record<string, unknown> | undefined;
  const count = (v: unknown, max = MAX_TOKENS): number => (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= max ? Math.floor(v) : 0);
  const row: RollupRow = {
    id: r.id,
    lastActivity: count(r.lastActivity, MAX_TS),
    turns: count(r.turns),
    toolCalls: count(r.toolCalls),
    tokens: { input: count(t?.input), output: count(t?.output), cacheRead: count(t?.cacheRead) },
  };
  if (r.usage && typeof r.usage === 'object') row.usage = r.usage as Session['usage'];
  if (r.liveUsage !== undefined && r.liveUsage !== null && typeof r.liveUsage === 'object') row.liveUsage = r.liveUsage;
  if (r.partial === true) row.partial = true;
  return row;
}
